import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { ChevronDownIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  STATE_COLOR_TONES,
  toneClass,
  type VersionStateDef,
} from "@/constants/version-state";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { mutateRequest, postFetcher, putFetcher } from "@/lib/swr";
import { cn } from "@/lib/utils";

// Create/edit one global-catalog state (version_state table). Code is
// immutable once created (it is the PK and lives inside version rows and app
// flows). Shared between the Admin States page and the per-app Flow editor
// (which creates into the same global catalog — codes mean the same thing in
// every app). Writes are admin-gated on the server.
export const StateDialog = ({
  row,
  rows,
  onClose,
  onCreated,
}: {
  row?: VersionStateDef;
  rows: VersionStateDef[];
  onClose: () => void;
  /** Called with the new code after a successful create (not on edit). */
  onCreated?: (code: number) => void;
}) => {
  const { toast } = useToast();
  const [code, setCode] = useState(row ? String(row.code) : "");
  const [label, setLabel] = useState(row?.label ?? "");
  const [color, setColor] = useState(row?.color ?? "none");
  const [nexts, setNexts] = useState<number[]>(row?.nexts ?? []);

  const onSaved = () => {
    mutateRequest("/version-states");
    if (!row) onCreated?.(Number(code));
    onClose();
  };
  const onError = (err: unknown) => toastError(toast, "Save failed", err);

  const { trigger: create, isMutating: creating } = useSWRMutation(
    "/version-states",
    postFetcher,
    { onSuccess: onSaved, onError }
  );
  const { trigger: update, isMutating: updating } = useSWRMutation(
    row ? `/version-states/${row.code}` : null,
    putFetcher,
    { onSuccess: onSaved, onError }
  );

  const targets = rows.filter((r) => String(r.code) !== code.trim());
  const toggleNext = (c: number) =>
    setNexts((prev) =>
      prev.includes(c)
        ? prev.filter((n) => n !== c)
        : [...prev, c].sort((a, b) => a - b)
    );

  const save = () => {
    const body = {
      label: label.trim(),
      color: color === "none" ? null : color,
      nexts,
    };
    if (row) update(body);
    else create({ ...body, code: Number(code) });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{row ? `Edit state ${row.label}` : "Add state"}</DialogTitle>
          <DialogDescription>
            Code value = flow order; leave gaps (e.g. 175 between 170 and 180)
            so later steps can slot in.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <label className="block w-24 space-y-1">
              <span className="text-sm text-muted-foreground">Code</span>
              <Input
                type="number"
                min={0}
                value={code}
                disabled={!!row}
                placeholder="175"
                onChange={(e) => setCode(e.target.value)}
                className="h-8 tabular-nums"
              />
            </label>
            <label className="block flex-1 space-y-1">
              <span className="text-sm text-muted-foreground">Label</span>
              <Input
                value={label}
                placeholder="已封板"
                onChange={(e) => setLabel(e.target.value)}
                className="h-8"
              />
            </label>
          </div>
          <div className="flex gap-3">
            <label className="block w-40 space-y-1">
              <span className="text-sm text-muted-foreground">Color</span>
              <Select value={color} onValueChange={setColor}>
                <SelectTrigger size="sm" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">default (muted)</SelectItem>
                  {STATE_COLOR_TONES.map((t) => (
                    <SelectItem key={t} value={t}>
                      <span className={cn("rounded px-1.5 py-0.5 text-xs", toneClass(t))}>
                        {t}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="block flex-1 space-y-1">
              <span className="text-sm text-muted-foreground">
                Default nexts
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-full justify-between gap-1 px-2 text-xs font-normal"
                  >
                    <span className="truncate">
                      {nexts.length
                        ? nexts
                            .map(
                              (c) =>
                                rows.find((r) => r.code === c)?.label ?? c
                            )
                            .join("、")
                        : "None (terminal)"}
                    </span>
                    <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {targets.map((r) => (
                    <DropdownMenuCheckboxItem
                      key={r.code}
                      checked={nexts.includes(r.code)}
                      onSelect={(e) => e.preventDefault()}
                      onCheckedChange={() => toggleNext(r.code)}
                    >
                      <span className="flex w-full items-center justify-between gap-3">
                        {r.label}
                        <span className="font-mono text-xs tabular-nums text-muted-foreground">
                          {r.code}
                        </span>
                      </span>
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={
              creating || updating || !label.trim() || (!row && code.trim() === "")
            }
            onClick={save}
          >
            {creating || updating ? "Saving…" : row ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StateDialog;
